import { FC } from "react";

// Import SVGs directly - works better with Turbopack
import DarkLogoSVG from "public/useit-dark.svg";
import DarkMidsummerLogoSVG from "public/useit-midsummer-dark.svg";
import LightMidsummerLogoSVG from "public/useit-midsummer.svg";
import DarkPrideLogoSVG from "public/useit-pride-dark.svg";
import LightPrideLogoSVG from "public/useit-pride.svg";
import LightLogoSVG from "public/useit.svg";

import { COMPANY_NAME } from "lib/constants";
import { countriesType } from "lib/types";

interface LogoProps extends React.SVGProps<SVGSVGElement> {
  /**
   * ARIA label for the Logo component.
   */
  ariaLabel?: string;

  /**
   * Additional class name for the Logo component.
   */
  className?: string;

  /**
   * The locale for the Logo component.
   */
  locale?: countriesType;

  translations: {
    constitutionDayAlt?: string;
    logo: string;
    midsummerLogoAlt: string;
    prideLogoAlt: string;
  };

  /**
   * The variant of the Logo component.
   * Possible values are: 'pride', 'midsummer', 'regular', 'constitutionDay'.
   */
  variant?: "pride" | "midsummer" | "regular" | "constitutionDay";
}

export const Logo: FC<LogoProps> = ({
  ariaLabel,
  className,
  translations,
  variant = "regular",
  ...other
}) => {
  const sharedProps = {
    viewBox: "0 0 351 352",
    width: 125,
    height: 125,
    role: "img",
  };
  const t = translations;

  const darkClasses = "hidden transition-all dark:block";
  const lightClasses = "block transition-all dark:hidden";

  // Render the appropriate logo based on variant
  switch (variant) {
    case "midsummer":
      return (
        <>
          <DarkMidsummerLogoSVG
            {...sharedProps}
            aria-label={ariaLabel || `${COMPANY_NAME} ${t.midsummerLogoAlt}`}
            className={`${darkClasses} ${className}`}
            {...other}
          />
          <LightMidsummerLogoSVG
            {...sharedProps}
            aria-label={ariaLabel || `${COMPANY_NAME} ${t.midsummerLogoAlt}`}
            className={`${lightClasses} ${className}`}
            {...other}
          />
        </>
      );
    case "constitutionDay":
      // For now, use the regular logo for Constitution Day
      // TODO: Add Constitution Day specific logo when available
      return (
        <>
          <DarkLogoSVG
            {...sharedProps}
            aria-label={
              ariaLabel || `${COMPANY_NAME} ${t.constitutionDayAlt || t.logo}`
            }
            className={`${darkClasses} ${className}`}
            {...other}
          />
          <LightLogoSVG
            {...sharedProps}
            aria-label={
              ariaLabel || `${COMPANY_NAME} ${t.constitutionDayAlt || t.logo}`
            }
            className={`${lightClasses} ${className}`}
            {...other}
          />
        </>
      );
    case "pride":
      return (
        <>
          <DarkPrideLogoSVG
            {...sharedProps}
            aria-label={ariaLabel || `${COMPANY_NAME} ${t.prideLogoAlt}`}
            className={`${darkClasses} ${className}`}
            {...other}
          />
          <LightPrideLogoSVG
            {...sharedProps}
            aria-label={ariaLabel || `${COMPANY_NAME} ${t.prideLogoAlt}`}
            className={`${lightClasses} ${className}`}
            {...other}
          />
        </>
      );
    default:
      return (
        <>
          <DarkLogoSVG
            {...sharedProps}
            aria-label={ariaLabel || `${COMPANY_NAME} ${t.logo}`}
            className={`${darkClasses} ${className}`}
            {...other}
          />
          <LightLogoSVG
            {...sharedProps}
            aria-label={ariaLabel || `${COMPANY_NAME} ${t.logo}`}
            className={`${lightClasses} ${className}`}
            {...other}
          />
        </>
      );
  }
};

export default Logo;
